import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { AUTH_URL } from 'src/configs';
import request from 'src/request';

/**
 * One entry per filter bar. `kind`, `status` and `currency_code` are sent as
 * they are; `address` and `id` are the two searches the API exposes through
 * its single `field`/`value` pair, so only one of them is ever non-empty (the
 * page clears the other when one is typed in).
 */
export type TransactionFilters = Record<string, string>;

const SEARCH_FIELDS = ['address', 'id'] as const;

interface TransactionsState {
  data: {
    transactions: object[];
    totalCount: number;
    totalPages: number;
    filters: TransactionFilters;
  };
  loading: boolean;
  error: string | null;
}

const initialState: TransactionsState = {
  data: {
    transactions: [],
    totalCount: 0,
    totalPages: 0,
    filters: {},
  },
  loading: false,
  error: null,
};

export const fetchTransactions = createAsyncThunk(
  'components/fetchTransactions',
  async ({
    page,
    filters,
    sort,
    order,
  }: {
    page: number;
    /** Already debounced and trimmed; each key is an API param, ANDed server-side. */
    filters?: TransactionFilters;
    sort?: string;
    order?: string;
  }) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '10',
    });

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (!value) return;
      if ((SEARCH_FIELDS as readonly string[]).includes(key)) {
        // The API takes one field/value pair; the page keeps these mutually
        // exclusive, so whichever is set wins without a tie to break.
        params.set('field', key);
        params.set('value', value);
        return;
      }
      params.set(key, value);
    });

    if (sort && order) {
      params.set('sort', sort);
      params.set('order', order);
    }

    const response = await request.get(`${AUTH_URL}/v1/admin/transactions?${params.toString()}`);
    return {
      data: response.data.transactions,
      totalCount: response.data.meta.total_count,
      totalPages: response.data.meta.page_count,
      page,
    };
  }
);

const transactionsData = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    updateFilter: (state, action: { payload: { key: string; value: string } }) => {
      const { key, value } = action.payload;
      state.data.filters = { ...state.data.filters, [key]: value };
    },
    resetFilters: (state) => {
      state.data.filters = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        // Filters are owned by updateFilter/resetFilters; a resolved request
        // must not overwrite what the admin has typed since it was fired.
        state.data.transactions = action.payload.data;
        state.data.totalCount = action.payload.totalCount;
        state.data.totalPages = action.payload.totalPages;
        state.loading = false;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch transactions';
      });
  },
});

export const { updateFilter, resetFilters } = transactionsData.actions;

export default transactionsData.reducer;
